/**
 * LacedUpChicago — Collection Page
 *
 * Enhances the server-rendered collection page in two narrow ways:
 *   1. turns the single in-flow filter form into a focus-managed
 *      drawer below 1000px;
 *   2. optionally auto-submits the native sort <select> on change.
 *
 * Everything else — filtering, sorting, active-filter removal and
 * pagination — is ordinary GET forms and links that work with this
 * script absent. There is no AJAX filtering, no product-grid
 * replacement, no history manipulation, no Load More and no infinite
 * scroll.
 *
 * Lifecycle notes (mirrors the pattern proven in assets/header.js):
 * Shopify replaces a section's markup wholesale on a Theme Editor
 * reload, so per-instance listeners bound inside initCollection() are
 * discarded with the old DOM. Document-level listeners outlive any
 * instance and are therefore registered exactly once. Drawer state can
 * also go stale without a normal close — a section reload while open,
 * or crossing the 1000px breakpoint where the drawer becomes a static
 * rail — so every exit path funnels through teardownDrawer() and no
 * scroll lock or dialog semantics can be stranded.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';
  var SCROLL_LOCK_CLASS = 'collection-scroll-lock';
  var activeSection = null;
  var desktopMql = window.matchMedia('(min-width: 1000px)');

  function isConnected(el) {
    return !!el && document.body.contains(el);
  }

  // Only genuinely rendered controls are Tab stops; this excludes
  // anything inside a collapsed <details> filter group, which would
  // otherwise skew the focus trap's first/last calculation.
  function isRendered(el) {
    return el.offsetParent !== null;
  }

  function getFocusable(container) {
    var candidates = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(candidates, isRendered);
  }

  function trapFocus(container, event) {
    var focusable = getFocusable(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Drops a reference to a section Shopify has already removed, and
  // reverses the one piece of global state a drawer can leave behind.
  function clearStaleSection() {
    if (activeSection && !isConnected(activeSection)) {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      activeSection = null;
    }
  }

  // Single shared exit path: explicit close, stale/disconnected
  // section, or crossing into the desktop rail breakpoint. Modal
  // semantics exist only while the drawer is genuinely acting as one,
  // so the no-JavaScript in-flow markup never carries them.
  function teardownDrawer(section, options) {
    options = options || {};
    var drawer = section.querySelector('[data-collection-drawer]');
    var trigger = section.querySelector('[data-collection-drawer-open]');
    var panel = drawer && drawer.querySelector('[data-collection-drawer-panel]');

    if (drawer) drawer.classList.remove(OPEN_CLASS);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
    }
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);

    if (activeSection === section) activeSection = null;
    if (options.returnFocus && trigger) trigger.focus();
  }

  function openDrawer(section) {
    var drawer = section.querySelector('[data-collection-drawer]');
    var trigger = section.querySelector('[data-collection-drawer-open]');
    var panel = drawer && drawer.querySelector('[data-collection-drawer-panel]');
    if (!drawer || !trigger || !panel) return;

    drawer.classList.add(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);

    activeSection = section;

    var closeButton = drawer.querySelector('[data-collection-drawer-close]');
    (closeButton || panel).focus();
  }

  function closeDrawer(section) {
    var drawer = section.querySelector('[data-collection-drawer]');
    if (!drawer || !drawer.classList.contains(OPEN_CLASS)) return;
    teardownDrawer(section, { returnFocus: true });
  }

  function initCollection(section) {
    if (section.dataset.collectionInitialized === 'true') return;
    section.dataset.collectionInitialized = 'true';

    clearStaleSection();

    // Signals to CSS that JS-only controls are now safe to reveal.
    section.setAttribute('data-collection-enhanced', '');

    var openTrigger = section.querySelector('[data-collection-drawer-open]');
    var closeTrigger = section.querySelector('[data-collection-drawer-close]');
    var backdrop = section.querySelector('[data-collection-drawer-backdrop]');
    var panel = section.querySelector('[data-collection-drawer-panel]');

    if (panel && !panel.hasAttribute('tabindex')) {
      panel.setAttribute('tabindex', '-1');
    }

    if (openTrigger) {
      openTrigger.addEventListener('click', function () {
        openDrawer(section);
      });
    }

    if (closeTrigger) {
      closeTrigger.addEventListener('click', function () {
        closeDrawer(section);
      });
    }

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeDrawer(section);
      });
    }

    // Sort auto-submit. The form's real submit button is only hidden
    // once this flag is set, so sorting stays operable if this script
    // never runs.
    var sortForm = section.querySelector('[data-collection-sort]');
    var sortSelect = section.querySelector('[data-collection-sort-select]');
    if (sortForm && sortSelect) {
      sortForm.setAttribute('data-sort-enhanced', '');
      sortSelect.addEventListener('change', function () {
        sortForm.submit();
      });
    }
  }

  var documentListenersBound = false;

  function bindDocumentListeners() {
    if (documentListenersBound) return;
    documentListenersBound = true;

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      clearStaleSection();

      var openDrawerEl = document.querySelector('[data-collection-drawer].is-open');
      if (openDrawerEl) {
        var section = openDrawerEl.closest('[data-collection]');
        if (section) closeDrawer(section);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      clearStaleSection();
      if (!activeSection) return;

      var drawer = activeSection.querySelector('[data-collection-drawer].is-open');
      if (drawer) trapFocus(drawer, event);
    });

    // Guards the trap against focus arriving from outside by means
    // other than Tab (e.g. a screen-reader virtual cursor).
    document.addEventListener('focusin', function (event) {
      clearStaleSection();
      if (!activeSection) return;

      var drawer = activeSection.querySelector('[data-collection-drawer].is-open');
      if (drawer && !drawer.contains(event.target)) {
        var focusable = getFocusable(drawer);
        if (focusable.length) focusable[0].focus();
      }
    });

    // At 1000px+ the same DOM becomes a static rail, so an open drawer
    // must be torn down or it would strand the scroll lock and focus
    // trap on a now-inline element.
    function handleBreakpointChange(event) {
      clearStaleSection();
      if (event.matches && activeSection) {
        teardownDrawer(activeSection, { returnFocus: false });
      }
    }

    if (desktopMql.addEventListener) {
      desktopMql.addEventListener('change', handleBreakpointChange);
    } else if (desktopMql.addListener) {
      // Safari <14 fallback.
      desktopMql.addListener(handleBreakpointChange);
    }

    // Fires immediately before Shopify removes a section in the Theme
    // Editor, so global state is released at that moment rather than
    // waiting for the next stale check.
    document.addEventListener('shopify:section:unload', function (event) {
      if (activeSection && event.target && event.target.contains(activeSection)) {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
        activeSection = null;
      }
    });
  }

  window.theme = window.theme || {};
  if (window.theme.onSectionLoad) {
    window.theme.onSectionLoad('[data-collection]', initCollection);
  }
  bindDocumentListeners();
})();
